import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const cudaTritonKernelsComponent: ComponentDef = {
  id: 'cuda-triton-kernels',
  name: 'CUDA & Triton Kernels',
  description: 'Fused GPU kernels for attention, matmul-activation, and custom element-wise ops.',
  category: 'optimization',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    {
      name: 'variant',
      label: 'Variant',
      type: 'select',
      default: 'gpu_execution',
      options: [
        { label: 'GPU Execution Model', value: 'gpu_execution' },
        { label: 'Triton Programming', value: 'triton_programming' },
        { label: 'Memory Access Patterns', value: 'memory_access' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const variant = String(params.variant);

    const g = new GraphBuilder();
    g.input('x', [seq_len, d_model]);

    if (variant === 'triton_programming') {
      // Triton programming model: show Triton kernel structure
      g.linear('w', 'x', [d_model, d_model]);
      g.matmul('gemm', 'x', 'w', [seq_len, d_model]);
      g.scale('block_ptr', 'gemm', 'Block Pointers');
      g.activation('act', 'block_ptr', 'SiLU');
      g.scale('autotune', 'act', 'Auto-tune Configs');
      g.scale('jit_compile', 'autotune', 'JIT Compile');
      g.output('out', 'jit_compile');
    } else if (variant === 'memory_access') {
      // Memory access patterns: coalesced vs strided, shared mem
      g.linear('w', 'x', [d_model, d_model]);
      g.matmul('gemm', 'x', 'w', [seq_len, d_model]);
      g.reshape('to_tiles', 'gemm', [seq_len / 4, 4, d_model]);
      g.scale('shared_mem', 'to_tiles', 'Shared Mem Load');
      g.activation('compute', 'shared_mem', 'Compute in SRAM');
      g.scale('coalesced_write', 'compute', 'Coalesced Write');
      g.scale('bank_conflict', 'coalesced_write', 'Avoid Bank Conflicts');
      g.output('out', 'bank_conflict');
    } else {
      // GPU execution model (default): warps, SMs, grid
      g.linear('w', 'x', [d_model, d_model]);
      g.matmul('gemm', 'x', 'w', [seq_len, d_model]);
      g.activation('act', 'gemm', 'SiLU');
      g.scale('fuse', 'act', 'Fused Kernel');
      g.output('out', 'fuse');
    }

    return g.build();
  }
};
